import React, { useEffect, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  UserFieldName,
  useInviteClientUser,
  UserRole,
  useDebounce,
  useCheckEmail,
  InviteUser_clientInviteUser,
  useResendUserInvitation,
} from '@modmd/data'
import { Box } from 'components/Layout'
import TextInput from 'components/TextInput'
import Button from 'components/Button'
import { UserDataForm } from 'internal-portal/routes/components/UserDataForm'
import Card from 'components/Card'
import { pxToRem, DEVICE, COLOR, fontSizes } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { Text } from 'components/Typography'
import Logo from 'components/Logo'
import { TermsAndConditions } from 'components/TermsAndConditions'
import { Checkbox } from 'components/Checkbox'
import { useToaster } from 'utils/useToaster'
import { ConsertTermsText } from 'utils/helpers'
import { Dialog } from 'components/Dialog'

const ERROR_MESSAGE = 'Please, Agree to Terms and Conditions'

interface Props {
  title: string
  subTitle?: string
  groupId?: string
  onSubmitLabel: string
  signInButtonLabel: string
  onSignInButtonClick: () => void
  onSuccess?: (user: InviteUser_clientInviteUser | null | undefined) => void
  onFailure?: () => void
}

export const UserInvitationCard: React.VFC<Props> = ({
  title,
  subTitle,
  groupId,
  onSubmitLabel,
  signInButtonLabel,
  onSignInButtonClick,
  onSuccess,
  onFailure,
}) => {
  const [isCheckedTOS, setIsCheckedTOS] = React.useState(false)
  const [resendEmailModalOpen, setResendEmailModalOpen] = useState(false)
  const [isCheckedHIPAA, setIsCheckedHIPAA] = React.useState(false)
  const [isConsentTerm, setIsConsentTerm] = React.useState(false)
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const [hasRecaptchaToken, setHasRecaptchaToken] = useState(false)
  const [emailAlreadyExist, setEmailAlreadyExist] = React.useState<string>()
  const { setToastMessage } = useToaster()

  const { userForm, isInviteNewUserLoading, captchaRef } = useInviteClientUser({
    onCompleted: (user) => {
      if (onSuccess) {
        onSuccess(user)
      }
    },
    onError: () => {
      if (onFailure) {
        onFailure()
      }
    },
  })

  const [checkEmail] = useCheckEmail({
    onCompleted: ({ checkEmail }) => {
      if (!checkEmail) {
        setEmailAlreadyExist('')
        return
      }
      setEmailAlreadyExist('This e-mail already exist')
      void userForm.setFieldValue('password', '')
      void userForm.setFieldValue('confirmationPassword', '')
    },
  })

  useDebounce(
    () => {
      if (!userForm.errors.email && userForm.dirty) {
        void userForm.setFieldTouched('password', false)
        checkEmail({
          variables: {
            email: userForm.values.email!,
          },
        })
      }
    },
    500,
    [userForm.values.email]
  )

  useEffect(() => {
    if (userForm.touched.email)
      checkEmail({
        variables: {
          email: userForm.values.email!,
        },
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userForm.values.email])

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } = userForm
  useEffect(() => {
    void userForm.setFieldValue('roles', [UserRole.PATIENT])
    void userForm.setFieldValue('groupId', groupId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [resendInvitation, { loading: resendIvitationLoading }] = useResendUserInvitation()

  const handleResendInvitation = async () => {
    try {
      if (values.email) await resendInvitation({ variables: { email: values.email } })
      setToastMessage(`Please check your inbox`, 'success')
    } catch (error) {
      // Do nothing
    }
  }

  return (
    <Card width="100%" maxWidth={isMobile ? '100%' : pxToRem(500)}>
      <Card.Title rightContent={<Logo isSmall />}>{title}</Card.Title>
      <form onSubmit={handleSubmit}>
        <Card.Content>
          <Box textAlign="center" mb="1rem">
            <Text fontSize={fontSizes.l}>{subTitle}</Text>
          </Box>
          <TextInput
            isFullWidth
            name={UserFieldName.EMAIL}
            value={values.email || ''}
            label="Email"
            onBlur={handleBlur}
            onPaste={() => {
              void userForm.setTouched({ email: true })
            }}
            onChange={handleChange}
            errorMessage={
              userForm.touched.email && userForm.errors.email
                ? userForm.errors.email
                : emailAlreadyExist
            }
          />
          <Box mt="1rem" display="flex" justifyContent="center">
            {emailAlreadyExist && (
              <Button
                onClick={() => {
                  setResendEmailModalOpen(true)
                }}
                isFetching={resendIvitationLoading}
              >
                Resend invitation e-mail
              </Button>
            )}
          </Box>
          {!emailAlreadyExist && (
            <>
              <TextInput
                isFullWidth
                name={UserFieldName.PASSWORD}
                value={values.password || ''}
                label="Password"
                type="password"
                onBlur={handleBlur}
                onChange={handleChange}
                errorMessage={touched.password && errors.password ? errors.password : undefined}
              />
              <TextInput
                isFullWidth
                name={UserFieldName.CONFIRMATION_PASSWORD}
                type="password"
                value={values.confirmationPassword || ''}
                label="Confirmation Password"
                onBlur={handleBlur}
                onChange={handleChange}
                errorMessage={
                  touched.confirmationPassword && errors.confirmationPassword
                    ? errors.confirmationPassword
                    : undefined
                }
              />
              <Box display="grid" gridGap={pxToRem(15)}>
                <UserDataForm userForm={userForm} />
                <Box>
                  <TermsAndConditions
                    isChecked={isCheckedHIPAA}
                    onChange={() => setIsCheckedHIPAA(!isCheckedHIPAA)}
                    url={process.env.REACT_APP_HIPAA_RELEASE_PDF}
                    urlLabel="HIPAA Consent and Release"
                    hasBottomContent={false}
                    errorMessage={!isCheckedHIPAA ? ERROR_MESSAGE : undefined}
                  />
                  <TermsAndConditions
                    isChecked={isCheckedTOS}
                    onChange={() => setIsCheckedTOS(!isCheckedTOS)}
                    url={process.env.REACT_APP_MODMDLA_TERMS}
                    text="I have read and agree to:"
                    hasBottomContent={false}
                    urlLabel="modMD's Terms and Conditions"
                    errorMessage={!isCheckedTOS ? ERROR_MESSAGE : undefined}
                  />
                  <Checkbox
                    checked={isConsentTerm}
                    onChange={() => setIsConsentTerm((prev) => !prev)}
                    label={ConsertTermsText}
                  />
                </Box>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                  ref={captchaRef}
                  onChange={(token) => {
                    setHasRecaptchaToken(!!token)
                  }}
                />
                <Button
                  type="submit"
                  disabled={
                    !!emailAlreadyExist ||
                    !!Object.keys(userForm.errors).length ||
                    isInviteNewUserLoading ||
                    !(isCheckedTOS && isCheckedHIPAA && isConsentTerm) ||
                    !hasRecaptchaToken
                  }
                >
                  {onSubmitLabel}
                </Button>
                <Box display="flex" justifyContent="center">
                  <Button onClick={onSignInButtonClick} appearance="link">
                    <Text color={COLOR.brand}>{signInButtonLabel}</Text>
                  </Button>
                </Box>
              </Box>
              <Box marginY="1rem" textAlign="center">
                <Button
                  size="small"
                  colorVariant="secondary"
                  appearance="link"
                  href={process.env.REACT_APP_PRIVACY_AND_POLICY}
                  target="_blank"
                >
                  <Text fontSize={fontSizes.xs}>{process.env.REACT_APP_PRIVACY_AND_POLICY}</Text>
                </Button>
              </Box>
            </>
          )}
          <Dialog
            variant={isMobile ? 'fullscreen' : 'center'}
            maxWidth={pxToRem(500)}
            isOpen={resendEmailModalOpen}
            onDismiss={() => setResendEmailModalOpen(false)}
          >
            <Box flexDirection="column" width="100%">
              <Card.Title onDismiss={() => setResendEmailModalOpen(false)}>
                Resend invitation e-mail
              </Card.Title>
              <Card.Content>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                  ref={captchaRef}
                  onChange={(token) => {
                    setHasRecaptchaToken(!!token)
                  }}
                />
              </Card.Content>
              <Card.Actions>
                <Button
                  colorVariant="secondary"
                  mr="0.25rem"
                  onClick={() => setResendEmailModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!hasRecaptchaToken}
                  onClick={async () => {
                    await handleResendInvitation()
                    setResendEmailModalOpen(false)
                  }}
                >
                  Send e-mail
                </Button>
              </Card.Actions>
            </Box>
          </Dialog>
        </Card.Content>
      </form>
    </Card>
  )
}
